import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AddIcon from "@mui/icons-material/Add";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import AuthService from "services/auth-service";
import dayjs from "dayjs";
import TransactionSaveModal from "layouts/dashboard/components/transactions/save-modal";
import TransactionSummary from "layouts/dashboard/components/transactions/summary";
import TransactionsList from "layouts/dashboard/components/transactions/list";
import { useMaterialUIController } from "context";

function Transactions() {
  const [controller] = useMaterialUIController();
  const { accounts } = controller;
  const [transactions, setTransactions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [selectedAccount, setSelectedAccount] = useState(accounts?.[0] || null);
  const [openModal, setOpenModal] = useState(false);
  const [editTransaction, setEditTransaction] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setSelectedAccount(accounts[0] ?? null);
  }, [accounts]);

  useEffect(() => {
    fetchTransactions();
  }, [selectedAccount, selectedMonth]);

  useEffect(() => {
    if (!openModal) {
      fetchTransactions();
      setEditTransaction(null);
    }
  }, [openModal]);

  const fetchTransactions = async () => {
    if (!selectedAccount || !selectedMonth) {
      return;
    }
    const month_gap = selectedAccount.type === "card" ? -1 : 0;
    const payload = {
      from: selectedMonth.set("date", selectedAccount.start_day).add(month_gap, "month").format("YYYY-MM-DD"),
      to: selectedMonth.set("date", selectedAccount.start_day).add(month_gap + 1, "month").format("YYYY-MM-DD"),
      account_id: selectedAccount.id,
    };
    const response = await AuthService.fetchTransactions(payload);
    setTransactions(response);
  };

  const handlePrevMonth = () => {
    setSelectedMonth((prev) => prev.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setSelectedMonth((prev) => prev.add(1, "month"));
  };

  const handleAccountChange = (event, newValue) => {
    setSelectedAccount(newValue);
  };

  const handleTotalChange = (newTotal) => {
    setTotal(newTotal);
  };

  const openCreateModal = () => {
    setEditTransaction(null);
    setOpenModal(true);
  };

  const openEditModal = (transaction) => {
    setEditTransaction(transaction);
    setOpenModal(true);
  };

  return (
    <>
      <TransactionSummary
        transactions={transactions}
        selectedAccount={selectedAccount}
        selectedMonth={selectedMonth}
        onTotalChange={handleTotalChange}
      />
      <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <MDBox width="200px">
          <Autocomplete
            options={accounts}
            getOptionLabel={(option) => option.name ?? "undefined"}
            value={selectedAccount}
            onChange={handleAccountChange}
            renderInput={(params) => (
              <TextField {...params} label="All accounts" variant="outlined" />
            )}
          />
        </MDBox>
        <MDBox display="flex" justifyContent="center" alignItems="center">
          <IconButton onClick={handlePrevMonth}>
            <ArrowBackIosIcon />
          </IconButton>
          <MDTypography variant="button" fontWeight="bold" color="text">
            {selectedMonth.format("MM/YYYY")}
          </MDTypography>
          <IconButton onClick={handleNextMonth}>
            <ArrowForwardIosIcon />
          </IconButton>
        </MDBox>
        <IconButton onClick={openCreateModal} disabled={!selectedAccount}>
          <AddIcon />
        </IconButton>
      </MDBox>
      <Card>
        <TransactionsList transactions={transactions} selectedAccount={selectedAccount} openEditModal={openEditModal} />
        <TransactionSaveModal
          open={openModal}
          handleClose={() => setOpenModal(false)}
          transaction={editTransaction}
          selectedAccount={selectedAccount}
          total={total}
        />
      </Card>
    </>
  );
}

export default Transactions;
